import React, { useEffect, useCallback, useState } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useForm, FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import { ButtonGroup } from '@paygreen/paygreen-ui';
import { useAPIError } from '../../../../context';
import { apiAuth } from '../../../../utils/hooks';
import {
    CancelButton,
    ValidateButton,
    ModalForm,
} from '../../../../components';
import UserCompanyBillingAddressForm from '../../../user/company/UserCompanyBillingAddressForm';

const AddressManagementModal = ({
    t,
    setOpen,
    isOpen,
    accountData,
    setAccountData,
    setIsLoading,
    isLoading,
    refresh,
}) => {
    const { addError } = useAPIError();
    const [isNew, setIsNew] = useState();

    const accountId = accountData?.representative?.accountId;

    const verifyAddress = () => {
        if (accountId) {
            apiAuth()
                .get(`/account/${accountId}/postalAddress/default`)
                .then(() => !isLoading && setIsNew(false))
                .catch(
                    (error) =>
                        error.response.data.status === 404 && !isLoading && setIsNew(true),
                );
        }
    };

    useEffect(verifyAddress, []);

    // initializing all react-hook-form values and methods
    const defaultValues = {
        addressLine1: accountData?.postalAddress?.addressLine1,
        addressLine2: accountData?.postalAddress?.addressLine2 || '',
        city: accountData?.postalAddress?.city,
        zipCode: accountData?.postalAddress?.zipCode,
        region: accountData?.postalAddress?.region || '',
        country: accountData?.postalAddress?.country || "FR",
    };

    const methods = useForm({ defaultValues });

    const { register, handleSubmit, reset, clearErrors } = methods;

    // Watch for changes on inputs
    useEffect(() => {
        register({ name: 'addressLine1' });
        register({ name: 'addressLine2' });
        register({ name: 'city' });
        register({ name: 'zipCode' });
        register({ name: 'region' });
        register({ name: 'country' });
    }, [register]);

    // Update selector default values in form when country data is retrieved to guarantee translation and form mounted
    useEffect(() => {
        reset({
            addressLine1: accountData?.postalAddress?.addressLine1,
            addressLine2: accountData?.postalAddress?.addressLine2 || '',
            city: accountData?.postalAddress?.city,
            zipCode: accountData?.postalAddress?.zipCode,
            region: accountData?.postalAddress?.region || '',
            country: accountData?.postalAddress?.country || "FR",
        });
    }, [accountData, reset, t]);

    /**
     * POST or PUT request to API to update billing address data
     * @returns {object} - object with billing address data updated
     */
    const updateBillingAddress = useCallback(
        (updatedBillingAddress) => {
            setIsLoading(true);
            let request = null;

            if (isNew) {
                request = apiAuth().post(
                    `/account/${accountId}/postalAddress`,
                    updatedBillingAddress,
                );
            } else {
                request = apiAuth().put(
                    `/account/${accountId}/postalAddress/default`,
                    updatedBillingAddress,
                );
            }

            request
                .then((res) => {
                    const newAddress = res.data;

                    setAccountData({
                        ...accountData,
                        postalAddress: newAddress,
                    });

                    refresh();
                    toast(t('form.toastSuccess.charityAdressUpdate'));
                })
                .catch((error) => {
                    const { detail, status } = error.response.data;

                    addError(detail, status);
                })
                .finally(() => setIsLoading(false));
        },
        [
            addError,
            setIsLoading,
            t,
            setAccountData,
            accountData,
            accountId,
            isNew,
            refresh
        ],
    );

    const submitForm = (data) => {
        const { addressLine1, addressLine2, city, zipCode, region, country } =
            data;

        const updatedBillingAddress = {
            addressLine1,
            addressLine2,
            city,
            zipCode,
            region,
            country,
        };

        updateBillingAddress(updatedBillingAddress);
    };

    const cancelCloseModal = () => {
        setOpen(false);

        // we need the reset methods to wait until modal is closed
        setTimeout(() => {
            clearErrors();
            reset(defaultValues);
        }, 500);
    };

    return (
        <ModalForm
            modalTitle={t(`user.company.billingAddress.ASSOCIATION`)}
            blockWidth="md"
            paddingLateral="md"
            isOpen={isOpen}
            actionOnClose={cancelCloseModal}
        >
            <FormProvider {...methods}>
                <UserCompanyBillingAddressForm
                    defaultValues={defaultValues}
                    onSubmit={handleSubmit(submitForm)}
                >
                    <ButtonGroup marginTop="sm" marginBottom="sm">
                        <CancelButton
                            onClick={cancelCloseModal}
                            buttonSize="md"
                        >
                            {t('form.button.cancel')}
                        </CancelButton>

                        <ValidateButton
                            type="submit"
                            buttonSize="md"
                            isDisabled={isLoading}
                        >
                            {t('form.button.save')}
                        </ValidateButton>
                    </ButtonGroup>
                </UserCompanyBillingAddressForm>
            </FormProvider>
        </ModalForm>
    );
};

AddressManagementModal.propTypes = {
    /**
     * @description setOpen and isOpen are automatically received from ModalControl parent
     */
    setOpen: PropTypes.func,
    isOpen: PropTypes.bool,
    accountData: PropTypes.object,
    setIsLoading: PropTypes.func,
    setAccountData: PropTypes.func,
    isShop: PropTypes.bool,
    refresh: PropTypes.func,
};

AddressManagementModal.defaultProps = {
    isOpen: false,
    isShop: true,
};

export default withTranslation('common')(AddressManagementModal);
